export type AdminApplicationId = 'sajug' | 'saju-ai' | 'presence' | 'q-language' | 'videoeditor' | 'office' | 'logo-mcp' | 'browser-agent' | 'ilhae' | 'embassy-watch';

export type AdminApplication = {
  id: AdminApplicationId;
  label: string;
  shortLabel: string;
  packageName: string;
  registrationApplication: string;
  /** 공개 홈페이지 주소. 있으면 사이트 방문(analytics) 페이지에서 고를 수 있다. */
  site?: string;
  paymentAppKeys: readonly string[];
  couponTargetSystem: string;
  defaultPlanCode: string;
  planCodes: readonly string[];
  capabilities: {
    accounts: true;
    coupons: boolean;
    metrics: true;
    operations?: true;
    content?: true;
    internalTesters?: 'sajug-fortune-engine' | 'q-language-play-review';
    /** UGOT Pro 내부 권한·결제 해지·환불 검토 화면(/apps/:id/ugot-pro). */
    ugotPro?: true;
    payPurchaseVerification: boolean;
    couponRedemption: boolean;
    productMetrics: boolean;
    /** pay 의 사업 지표(매출·구독·퍼널·할인·경보) 섹션. */
    businessMetrics?: true;
  };
};

export const DEFAULT_ADMIN_APPLICATION_ID: AdminApplicationId = 'sajug';

export const ADMIN_APPLICATIONS: readonly AdminApplication[] = [
  {
    id: 'sajug',
    label: '사주앱',
    shortLabel: '사주',
    packageName: 'com.sajug.app',
    registrationApplication: 'SAJUG',
    paymentAppKeys: ['SAJU', 'SAJUG'],
    couponTargetSystem: 'SAJUG',
    defaultPlanCode: 'SAJU_PRO',
    planCodes: [
      'SAJU_PERSONAL',
      'SAJU_PERSONAL_YEARLY',
      'SAJU_PLUS',
      'SAJU_PLUS_YEARLY',
      'SAJU_PRO',
      'SAJU_PRO_YEARLY',
    ],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      internalTesters: 'sajug-fortune-engine',
      payPurchaseVerification: true,
      couponRedemption: true,
      productMetrics: true,
      businessMetrics: true,
    },
  },
  {
    id: 'saju-ai',
    label: '다온 사주',
    shortLabel: '다온',
    packageName: 'uk.ugot.daon',
    registrationApplication: 'SAJU_AI',
    paymentAppKeys: ['saju-ai'],
    couponTargetSystem: '',
    defaultPlanCode: '',
    planCodes: [],
    capabilities: {
      accounts: true,
      coupons: false,
      metrics: true,
      payPurchaseVerification: true,
      couponRedemption: false,
      productMetrics: false,
    },
  },
  {
    id: 'presence',
    label: 'Presence',
    shortLabel: 'Presence',
    packageName: 'app.pnp.presence',
    registrationApplication: 'PRESENCE',
    paymentAppKeys: ['PRESENCE'],
    couponTargetSystem: 'PRESENCE',
    defaultPlanCode: 'PRESENCE_PLUS_MONTHLY',
    planCodes: ['PRESENCE_PLUS', 'PRESENCE_PLUS_MONTHLY', 'PRESENCE_PLUS_YEARLY'],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      payPurchaseVerification: true,
      couponRedemption: false,
      productMetrics: true,
    },
  },
  {
    id: 'q-language',
    label: 'Q 언어',
    shortLabel: 'Q 언어',
    packageName: 'com.edusense.language.app',
    registrationApplication: 'Q_LANGUAGE',
    paymentAppKeys: ['Q_LANGUAGE', 'Q-LANGUAGE', 'EDUSENSE'],
    couponTargetSystem: 'EDUSENSE',
    defaultPlanCode: 'Q_LANGUAGE_PLUS',
    planCodes: ['Q_LANGUAGE_PLUS', 'Q_LANGUAGE_PLUS_ANNUAL'],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      operations: true,
      content: true,
      internalTesters: 'q-language-play-review',
      payPurchaseVerification: true,
      couponRedemption: false,
      productMetrics: false,
    },
  },
  {
    // 데스크톱 영상 편집기. identity-server 의 OAuth 클라이언트 `videoeditor` 로 로그인한 계정.
    // 결제·쿠폰은 아직 Pay 에 앱 키가 없다 — 키는 예약만 해 두고 검증 기능은 끈다.
    id: 'videoeditor',
    label: '영상 편집기',
    shortLabel: '영상',
    packageName: 'uk.ugot.videoeditor',
    registrationApplication: 'VIDEOEDITOR',
    site: 'https://videoeditor.ugot.uk',
    paymentAppKeys: ['VIDEOEDITOR'],
    couponTargetSystem: 'VIDEOEDITOR',
    defaultPlanCode: 'VIDEOEDITOR_PLUS',
    planCodes: ['VIDEOEDITOR_PLUS'],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      payPurchaseVerification: false,
      couponRedemption: false,
      productMetrics: false,
    },
  },
  {
    // office.ugot.uk(문서·시트 편집기)와 문서 MCP. saju-web 클라이언트로 로그인하지만 redirect 호스트가
    // office 라 identity-server 가 OFFICE 로 기록한다.
    id: 'office',
    label: '오피스',
    shortLabel: '오피스',
    packageName: 'uk.ugot.office',
    registrationApplication: 'OFFICE',
    paymentAppKeys: ['OFFICE'],
    couponTargetSystem: 'OFFICE',
    defaultPlanCode: 'OFFICE_PLUS',
    planCodes: ['OFFICE_PLUS'],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      payPurchaseVerification: false,
      couponRedemption: false,
      productMetrics: false,
    },
  },
  {
    // 로고 MCP(logomcp.ugot.uk). 커넥터는 동적 등록 클라이언트라 id 가 매번 달라, identity-server 가
    // 인가 요청의 resource(RFC 8707) 호스트로 LOGO_MCP 를 기록한다.
    id: 'logo-mcp',
    label: '로고 MCP',
    shortLabel: '로고',
    packageName: 'uk.ugot.logomcp',
    registrationApplication: 'LOGO_MCP',
    paymentAppKeys: ['LOGO_MCP'],
    couponTargetSystem: 'LOGO_MCP',
    defaultPlanCode: 'LOGO_MCP_PLUS',
    planCodes: ['LOGO_MCP_PLUS'],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      payPurchaseVerification: false,
      couponRedemption: false,
      productMetrics: false,
    },
  },
  {
    // Ugot Browser Agent(Chrome 확장 + 브릿지). 홈페이지는 agent.ugot.uk, 로그인은 브릿지 계정.
    // 유료 상품은 플러그인 전체 묶음 UGOT Pro(UGOT_PRO·UGOT_PRO_YEARLY, Paddle). 구매 검증 기능은 아직 끈다.
    id: 'browser-agent',
    label: 'Browser Agent · UGOT Pro',
    shortLabel: '에이전트',
    packageName: 'uk.ugot.browseragent',
    registrationApplication: 'BROWSER_AGENT',
    site: 'https://agent.ugot.uk',
    // pay 계정 화면은 UGOT Pro 주문을 UGOT_PRO 로 묶어 보낸다.
    paymentAppKeys: ['BROWSER_AGENT', 'UGOT_PRO', 'BROWSER_AGENT_PRO'],
    couponTargetSystem: 'BROWSER_AGENT',
    defaultPlanCode: 'UGOT_PRO',
    planCodes: ['UGOT_PRO', 'UGOT_PRO_YEARLY'],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      payPurchaseVerification: false,
      couponRedemption: false,
      productMetrics: false,
      businessMetrics: true,
      ugotPro: true,
    },
  },
  {
    // 일해라.ai 데스크톱 앱(ilhae.ugot.uk). 아직 계정 로그인·결제가 없고 홈페이지의 문의 폼과
    // 방문 집계만 쓴다 — 키는 예약만 해 두고 검증 기능은 끈다.
    id: 'ilhae',
    label: '일해라.ai',
    shortLabel: '일해',
    packageName: 'uk.ugot.ilhae',
    registrationApplication: 'ILHAE',
    site: 'https://ilhae.ugot.uk',
    paymentAppKeys: ['ILHAE'],
    couponTargetSystem: 'ILHAE',
    defaultPlanCode: 'ILHAE_PLUS',
    planCodes: ['ILHAE_PLUS'],
    capabilities: {
      accounts: true,
      coupons: true,
      metrics: true,
      payPurchaseVerification: false,
      couponRedemption: false,
      productMetrics: false,
    },
  },
  {
    // 주한 대사관 철수 1분 실시간 모니터링(watch.ugot.uk / embassy.ugot.uk).
    // 무로그인 공개 웹/PWA이므로 사이트 방문 집계와 실시간 접속·푸시 구독 지표를 확인한다.
    id: 'embassy-watch',
    label: 'Embassy Watch',
    shortLabel: '대사관',
    packageName: 'uk.ugot.embassywatch',
    registrationApplication: 'EMBASSY_WATCH',
    site: 'https://watch.ugot.uk',
    paymentAppKeys: ['EMBASSY_WATCH'],
    couponTargetSystem: 'EMBASSY_WATCH',
    defaultPlanCode: '',
    planCodes: [],
    capabilities: {
      accounts: true,
      coupons: false,
      metrics: true,
      payPurchaseVerification: false,
      couponRedemption: false,
      productMetrics: true,
    },
  },
] as const;

/** 공개 사이트가 있는 앱만 — 사이트 방문 페이지의 선택지. 순서는 등록 순서를 따른다. */
export function getSiteAnalyticsApplications(): AdminApplication[] {
  return ADMIN_APPLICATIONS.filter((application) => Boolean(application.site));
}

const APPLICATIONS_BY_ID = new Map(ADMIN_APPLICATIONS.map((application) => [application.id, application]));

export function isAdminApplicationId(value: string | null | undefined): value is AdminApplicationId {
  return Boolean(value && APPLICATIONS_BY_ID.has(value as AdminApplicationId));
}

export function getAdminApplication(applicationId: AdminApplicationId): AdminApplication {
  return APPLICATIONS_BY_ID.get(applicationId) ?? ADMIN_APPLICATIONS[0];
}

export function resolveAdminApplicationIdFromPath(pathname: string): AdminApplicationId | null {
  const match = pathname.match(/^\/apps\/([^/]+)(?:\/|$)/);
  return match && isAdminApplicationId(match[1]) ? match[1] : null;
}

export type AdminApplicationSection = 'accounts' | 'coupons' | 'metrics' | 'operations' | 'internal-testers' | 'content' | 'ugot-pro' | 'usage-credits';

export function buildAdminApplicationPath(applicationId: AdminApplicationId, section: AdminApplicationSection = 'accounts'): string {
  const application = getAdminApplication(applicationId);
  const safeSection = (section === 'coupons' && !application.capabilities.coupons)
    || (section === 'operations' && !application.capabilities.operations)
    || (section === 'internal-testers' && !application.capabilities.internalTesters)
    || (section === 'content' && !application.capabilities.content)
    || (section === 'ugot-pro' && !application.capabilities.ugotPro)
    || (section === 'usage-credits' && applicationId !== 'saju-ai')
    ? 'accounts'
    : section;
  return `/apps/${applicationId}/${safeSection}`;
}

export function switchAdminApplicationPath(pathname: string, applicationId: AdminApplicationId): string {
  const match = pathname.match(/^\/apps\/[^/]+\/(accounts|coupons|metrics|operations|internal-testers|content|ugot-pro|usage-credits)(\/.*)?$/);
  if (!match) {
    return buildAdminApplicationPath(applicationId);
  }
  return `${buildAdminApplicationPath(applicationId, match[1] as AdminApplicationSection)}${match[2] ?? ''}`;
}

export function matchesAdminPaymentAppKey(applicationId: AdminApplicationId, appKey: string): boolean {
  const normalized = appKey.trim().toUpperCase().replace(/_/g, '-');
  return getAdminApplication(applicationId).paymentAppKeys.some(
    (candidate) => candidate.toUpperCase().replace(/_/g, '-') === normalized,
  );
}

export function matchesAdminRegistrationApplication(
  applicationId: AdminApplicationId,
  registrationApplications: readonly string[] | string | null | undefined,
): boolean {
  if (!registrationApplications) return false;
  const candidates = Array.isArray(registrationApplications)
    ? registrationApplications
    : [registrationApplications];
  return candidates.some(
    (candidate) => getAdminApplication(applicationId).registrationApplication === candidate.trim().toUpperCase(),
  );
}

export function matchesAdminPlanCode(applicationId: AdminApplicationId, planCode: string | null | undefined): boolean {
  if (!planCode) return false;
  const normalized = planCode.trim().toUpperCase();
  return getAdminApplication(applicationId).planCodes.some((candidate) => candidate.toUpperCase() === normalized);
}
